/**
 * Scroll-driven hero — the "Home Screen" template's signature move:
 * full-bleed image with a giant display word that collapses into a bento
 * grid as you scroll.
 *
 * Rebuilt 2026-06-12 after visual QA found the ported math misaligned:
 * the template's -15% side-column offset and bottom-padding hacks shoved
 * the side columns out of line with the center panel. Geometry here is a
 * single centered row (items-stretch) whose height shrinks uniformly, so
 * every column shares the same top and bottom edge at every scroll state.
 * Imagery is the congregation, not empty landscapes: real people praying,
 * reading, and walking together.
 */
import { useEffect, useRef, useState } from "react";
// Hero imagery bundled locally (AI-11994) — external Unsplash URLs did not load in
// the network-restricted CI runner, tripping the home-page smoke tests. Vite hashes
// and serves these from the build, so they load reliably offline and in CI.
import heroCongregation from "@/assets/landing/1438232992991-995b7058bbb3.jpg";
import sideBible from "@/assets/landing/1504052434569-70ad5836ab65.jpg";
import sidePrayer from "@/assets/landing/1609234656388-0ff363383899.jpg";
import sideLaughing from "@/assets/landing/1517486808906-6ca8b3f04846.jpg";
import sideFriends from "@/assets/landing/1529156069898-49953e39b3ac.jpg";

const WORD = "JESUS";

const sideImages = [
  { src: sideBible, alt: "Hands holding an open Bible during a service", position: "left" },
  { src: sidePrayer, alt: "A group gathered close in prayer", position: "left" },
  { src: sideLaughing, alt: "A young group laughing together outside", position: "right" },
  { src: sideFriends, alt: "Friends arm in arm looking ahead together", position: "right" },
];

export function ScrollHero() {
  const sectionRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const scrollableHeight = window.innerHeight * 2;
      setScrollProgress(Math.max(0, Math.min(1, -rect.top / scrollableHeight)));
    };
    const handleScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const textOpacity = Math.max(0, 1 - scrollProgress / 0.2);
  const p = Math.max(0, Math.min(1, (scrollProgress - 0.2) / 0.8)); // image progress

  // One centered row; height shrinks uniformly so columns stay edge-aligned.
  const rowHeight = 100 - p * 28; // 100% -> 72%
  const centerWidth = 100 - p * 52; // 100% -> 48%
  const sideWidth = p * 21; // 0% -> 21%
  const gap = p * 16;
  const borderRadius = p * 24;
  const sideSlideLeft = -100 + p * 100; // -100% -> 0
  const sideSlideRight = 100 - p * 100; // 100% -> 0

  return (
    <section ref={sectionRef} className="relative bg-[#FAFAF7]">
      <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
        <div
          className="flex w-full items-stretch justify-center"
          style={{ height: `${rowHeight}%`, gap: `${gap}px`, paddingLeft: `${p * 16}px`, paddingRight: `${p * 16}px` }}
        >
          {/* Left column */}
          <div
            className="flex flex-col will-change-transform"
            style={{
              width: `${sideWidth}%`,
              gap: `${gap}px`,
              transform: `translate3d(${sideSlideLeft}%, 0, 0)`,
              opacity: p,
              backfaceVisibility: "hidden",
            }}
          >
            {sideImages
              .filter((img) => img.position === "left")
              .map((img) => (
                <div
                  key={img.alt}
                  className="relative flex-1 overflow-hidden"
                  style={{ borderRadius: `${borderRadius}px` }}
                >
                  <img src={img.src} alt={img.alt} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
                </div>
              ))}
          </div>

          {/* Center: the congregation */}
          <div
            className="relative overflow-hidden will-change-transform"
            style={{ width: `${centerWidth}%`, borderRadius: `${borderRadius}px` }}
          >
            <img
              src={heroCongregation}
              decoding="async"
              loading="eager"
              alt="A congregation in worship, one hand raised toward the light"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/15 to-black/10" />

            {/* Giant word — fades out first */}
            <div
              className="absolute inset-0 flex items-end justify-center overflow-hidden pb-[3vh]"
              style={{ opacity: textOpacity }}
            >
              <h1 aria-label={WORD} className="text-[22vw] font-medium leading-[0.9] tracking-tighter text-white">
                {WORD.split("").map((letter, index) => (
                  <span
                    key={index}
                    aria-hidden
                    className="inline-block animate-[heroSlideUp_0.8s_ease-out_forwards] opacity-0"
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    {letter}
                  </span>
                ))}
              </h1>
            </div>
          </div>

          {/* Right column */}
          <div
            className="flex flex-col will-change-transform"
            style={{
              width: `${sideWidth}%`,
              gap: `${gap}px`,
              transform: `translate3d(${sideSlideRight}%, 0, 0)`,
              opacity: p,
              backfaceVisibility: "hidden",
            }}
          >
            {sideImages
              .filter((img) => img.position === "right")
              .map((img) => (
                <div
                  key={img.alt}
                  className="relative flex-1 overflow-hidden"
                  style={{ borderRadius: `${borderRadius}px` }}
                >
                  <img src={img.src} alt={img.alt} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Scroll space driving the animation */}
      <div className="h-[200vh]" />

      {/* Tagline — soft sky wash so the drop out of the hero isn't flat white (AI-11994) */}
      <div className="relative overflow-hidden bg-sky-fade px-6 pb-28 pt-32 md:px-12 md:pb-36 md:pt-48 lg:px-20 lg:pb-44 lg:pt-56">
        <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[760px] -translate-x-1/2 bg-sky-glow blur-3xl" />
        <p className="relative mx-auto max-w-2xl text-center text-2xl leading-relaxed text-neutral-500 md:text-3xl lg:text-[2.5rem] lg:leading-snug">
          …entered the chat.
          <br />
          <span className="text-neutral-900">A companion for walking with God, daily.</span>
        </p>
      </div>

      <style>{`
        @keyframes heroSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </section>
  );
}
